import {
  DestroyRef,
  effect,
  inject,
  Injector,
  Signal,
  signal,
  WritableSignal,
} from '@angular/core';
import {
  AttributionControl,
  GeoJSONSource,
  GeolocateControl,
  LayerSpecification,
  LngLatLike,
  Map,
  MapGeoJSONFeature,
  MapLayerMouseEvent,
  setWorkerUrl,
  StyleSpecification,
} from 'maplibre-gl';
import { MapSourceId, Point } from '@daisi/models/map';
import { LoggingService } from '@daisi/services/logging/logging.service';
import { environment } from '@env/environment';
import { MapLayersApiService } from '@services/map/map-layers-api.service';
import { MapLayerOrderingService } from '@services/map/map-layer-ordering.service';
import { attachPanPreview } from '@services/map/pan-preview';
import { FCD_VECTOR_STYLE, HECTOMETRE_STYLE } from '@pages/overview-page/layers/vector-layers';
import { MapBackgroundOption, MapDisplayOption } from '@ndwnu/design-system';
import { UserStateService } from '@services/user-state/user-state.service';
import { AuthService } from '@services/auth/auth.service';
import { BBox, FeatureCollection, Polygon } from 'geojson';
import { BannerService } from '@components/layout/banner.service';
import { FcdFreshnessService } from '@services/map/fcd-freshness.service';
import { NavigationService } from '@services/navigation/navigation.service';
import { GeoJSONSourceSpecification } from '@maplibre/maplibre-gl-style-spec';
import { NDWMapStyle, NDWOSMMapStyle } from '@ndwnu/map';

export const BackgroundStyle = {
  NDW_DEFAULT: {
    id: 'ndw-basemap',
    symbolsId: 'ndw-basemap-symbols',
    name: 'NWB',
    description: 'NDW NWB Basemap',
    style: NDWMapStyle,
    ignoreLayersWithPrefix: ['nwb-hectometre-signs'],
    imageLink: './assets/map-background-icon/ndw.png',
  } as MapStyleConfiguration,
  NDW_OSM_DEFAULT: {
    id: 'ndw-osm-basemap',
    symbolsId: 'ndw-osm-basemap-symbols',
    name: 'OSM',
    description: 'NDW OSM Basemap',
    style: NDWOSMMapStyle,
    ignoreLayersWithPrefix: ['nwb-hectometre-signs'],
    imageLink: './assets/map-background-icon/ndw.png',
  } as MapStyleConfiguration,
  OSM_RASTER: {
    id: 'osm-raster-id',
    symbolsId: 'osm-raster-symbols',
    name: 'OSM detail',
    description: 'OSM raster',
    style: '/assets/map-style/osm-raster.json',
    imageLink: './assets/map-background-icon/osm-raster.png',
  } as MapStyleConfiguration,
  OSM_FRONT_OFFICE_LIGHT_WITH_NWB_ROADS: {
    id: 'osm-front-office-light-with-nwb-roads-id',
    symbolsId: 'osm-front-office-light-with-nwb-roads-symbols',
    name: 'Licht',
    description: 'OSM front office light met NWB wegen',
    style: 'https://maps.ndw.nu/styles/osm-front-office-light-with-nwb-roads.json',
    imageLink: './assets/map-background-icon/osm-front-office-light-with-nwb-roads.png',
  } as MapStyleConfiguration,
  OSM_FRONT_OFFICE_DARK: {
    id: 'osm-front-office-dark-id',
    symbolsId: 'osm-front-office-dark-symbols',
    name: 'Donker',
    description: 'OSM front office dark',
    style: 'https://maps.ndw.nu/styles/osm-front-office-dark.json',
    imageLink: './assets/map-background-icon/osm-front-office-dark.png',
  } as MapStyleConfiguration,
  OSM_GREEN: {
    id: 'osm-green-id',
    symbolsId: 'osm-green-symbols',
    name: 'Groen',
    description: 'OSM groen',
    style: 'https://maps.ndw.nu/styles/osm-green.json',
    imageLink: './assets/map-background-icon/osm-green.png',
  } as MapStyleConfiguration,
  AERIAL: {
    id: 'aerial-id',
    symbolsId: 'aerial-symbols',
    name: 'Luchtfoto',
    description: 'Luchtfoto',
    style: '/assets/map-style/aerial.json',
    imageLink: './assets/map-background-icon/aerial.png',
  } as MapStyleConfiguration,
};

export const FCD_MAP_OPTION = {
  id: FCD_VECTOR_STYLE.id,
  name: 'Actueel verkeer',
  imageLink: './assets/map-option-icon/fcd.png',
};
export const HECTOMETRE_MAP_OPTION = {
  id: HECTOMETRE_STYLE.id,
  name: 'Hectometrering',
  imageLink: 'https://maps.ndw.nu/styles/layers/nwb/hectometreSigns/hectometre-shields.png',
};

export interface MapOption extends MapDisplayOption {
  realtime?: boolean;
}

export interface MapStyleConfiguration extends MapBackgroundOption<string | StyleSpecification> {
  ignoreLayersWithPrefix?: string[];
  symbolsId: string;
  forceVisibleLayerIds?: string[];
}

// maplibre-gl derives its worker URL from `import.meta.url`, which after bundling points at our own
// bundle rather than at the maplibre dist folder. Point it at the copy angular.json puts in the
// output instead; `maplibre-gl-shared.mjs` is copied alongside it because the worker imports it.
setWorkerUrl('/maplibre-gl/dist/maplibre-gl-worker.mjs');

export abstract class AbstractMapService {
  protected readonly log = inject(LoggingService);
  protected readonly injector = inject(Injector);
  protected readonly mapLayersApiService = inject(MapLayersApiService);
  protected readonly mapLayerOrderingService = inject(MapLayerOrderingService);
  protected readonly userStateService = inject(UserStateService);
  protected readonly authService = inject(AuthService);
  protected readonly bannerService = inject(BannerService);
  protected readonly destroyRef = inject(DestroyRef);
  private readonly fcdFreshnessService = inject(FcdFreshnessService);
  protected readonly navigation = inject(NavigationService);

  readonly map = signal<Map>(undefined as unknown as Map);
  readonly loadingDone = signal(false);
  readonly bearing = signal(0);
  readonly pitch = signal(0);

  // Mirrors the GeolocateControl's tracking state for the non-navigation recenter path below.
  private following = false;

  protected showCurrentLocationButton = true;
  protected showAttribution = true;
  initiateWithUserBounds = true;
  showZoomToContent = true;
  showNavigationMode = true;

  readonly activeMapBackground = signal<MapStyleConfiguration>(
    undefined as unknown as MapStyleConfiguration,
  );

  readonly CONTROLS_LOCATION = 'bottom-left';
  readonly COORDINATE_COMPARISON_PRECISION = 0.000001; // ~ 10 cm
  readonly mapTranslations = {
    'NavigationControl.ResetBearing': 'Herstel richting naar het noorden',
    'NavigationControl.ZoomIn': 'Inzoomen',
    'NavigationControl.ZoomOut': 'Uitzoomen',
    'AttributionControl.ToggleAttribution': 'Toeschrijvingen tonen/verbergen',
  };
  protected readonly EMPTY_GEOJSON_SOURCE: Readonly<GeoJSONSourceSpecification> = {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: [],
    },
  };

  readonly mapOptions = signal<MapOption[]>([]);
  abstract readonly activeMapOptions: WritableSignal<string[]>;

  initialize(container: HTMLElement, enableRotation = true) {
    if (this.map()) {
      this.map().remove();
    }
    this.navigation.detach();
    this.following = false;

    const emptyStyle = {
      version: 8,
      sources: {},
      layers: [],
      sprite: 'https://maps.ndw.nu/styles/sprites/osm-default/osm-default',
      glyphs: 'https://maps.ndw.nu/styles/glyphs/{fontstack}/{range}.pbf',
    } as StyleSpecification;

    const map = new Map({
      container,
      style: emptyStyle,
      renderWorldCopies: false,
      zoom: environment.map?.zoom ?? 9,
      center: environment.map?.center ?? {
        lng: 5.1506959,
        lat: 52.094979,
      },
      locale: this.mapTranslations,
      dragRotate: enableRotation,
      // The work item asks for heading rotation only; pitch (tilt) is not requested and would
      // both flatten the map-aligned icons at high tilt and mislabel the reset button. Locking
      // maxPitch to 0 disables every pitch gesture (drag, keyboard and two-finger).
      maxPitch: 0,
      attributionControl: false,
      transformRequest: (url, resourceType) => {
        if (url.includes('/api/tiles')) {
          return {
            url,
            method: 'GET',
            headers: { Authorization: 'Bearer ' + this.authService.accessToken() },
          };
        }
        return { url };
      },
    });
    // The service is a root singleton but MapComponent re-mounts per route/side panel, so a fresh
    // map is built here each time. Seed the signals from the new map, otherwise a bearing left over
    // from a previous map would render a stale, unclearable compass over a north-up map.
    this.bearing.set(map.getBearing());
    this.pitch.set(map.getPitch());
    if (!enableRotation) {
      // No reset-north control ships on these maps, so lock the heading to prevent a rotated view
      // the user cannot recover from (keyboard and two-finger gestures).
      map.keyboard.disableRotation();
      map.touchZoomRotate.disableRotation();
    }
    if (this.showAttribution) {
      const attributionControl = new AttributionControl({ compact: true });
      map.addControl(attributionControl, this.CONTROLS_LOCATION);
      const collapseAttribution = () => {
        if (attributionControl._container?.classList.contains('maplibregl-compact-show')) {
          attributionControl._updateCompactMinimize();
          map.off('styledata', collapseAttribution);
          map.off('sourcedata', collapseAttribution);
        }
      };
      map.on('styledata', collapseAttribution);
      map.on('sourcedata', collapseAttribution);
    }
    let geolocateControl: GeolocateControl | undefined;
    if (this.showCurrentLocationButton) {
      geolocateControl = new GeolocateControl({
        positionOptions: { enableHighAccuracy: true },
        trackUserLocation: true,
      });
      map.addControl(geolocateControl);

      // By default the GeolocateControl zooms in to ~level 15 once it gets a location. We only want
      // to recenter and keep the current zoom level. This should only happen while the user is
      // actually following their location (ACTIVE_LOCK): if the user drags the map away, the control
      // moves to BACKGROUND and following must stop. trackuserlocationstart/-end mirror that button
      // state exactly, so the UI and behaviour stay in sync. Zooming (buttons and pinch) keeps the
      // lock because the control itself exempts it via map.isZooming(), so it keeps recentering.
      geolocateControl.on('trackuserlocationstart', () => {
        this.following = true;
      });
      geolocateControl.on('trackuserlocationend', () => {
        this.following = false;
      });
      geolocateControl.on(
        'geolocate',
        (event: { coords: GeolocationCoordinates; timestamp: number }) => {
          if (this.navigation.active()) {
            return;
          }
          if (!this.following) {
            return;
          }
          const { longitude, latitude } = event.coords;
          map.easeTo(
            { center: [longitude, latitude], zoom: map.getZoom() },
            { geolocateSource: true },
          );
        },
      );
    }

    attachPanPreview(map);

    map.on('rotate', () => this.bearing.set(map.getBearing()));
    map.on('pitch', () => this.pitch.set(map.getPitch()));
    map.on('style.load', this.loadedMapCallback.bind(this));

    this.map.set(map);
    this.navigation.attach(map);
  }

  resetNorth() {
    this.map()?.resetNorthPitch();
  }

  private loadBackground() {
    const mapBackgroundConfigId = this.userStateService.state.mapBackgroundConfigId();

    if (this.map()) {
      const config =
        Object.values(BackgroundStyle).find((value) => value.id === mapBackgroundConfigId) ||
        BackgroundStyle.NDW_OSM_DEFAULT;

      this.activeMapBackground.set(config);
      this.mapLayersApiService.switchMapBackground(this.map(), config);
    }
  }

  private loadActiveLayers() {
    const mapOptions = this.mapOptions();
    const activeMapOptions = this.activeMapOptions();
    const background = this.activeMapBackground();

    if (this.map()?.getStyle()) {
      const forceVisibleLayerIds = new Set(
        (background?.forceVisibleLayerIds ?? []).map((id) => `${background.id}-${id}`),
      );

      mapOptions.forEach((option) => {
        this.map()
          .getStyle()
          .layers.forEach((layer) => {
            if (forceVisibleLayerIds.has(layer.id)) {
              return;
            }
            if (layer.id.includes(option.id)) {
              this.map().setLayoutProperty(
                layer.id,
                'visibility',
                activeMapOptions.some((a) => a === option.id) ? 'visible' : 'none',
              );
            }
          });
      });
    }
  }

  protected loadedMapCallback() {
    this.map().resize();
    effect(
      () => {
        this.loadBackground();
      },
      { injector: this.injector },
    );
    effect(
      () => {
        this.loadActiveLayers();
      },
      { injector: this.injector },
    );

    this.addHectometreLayer();

    this.map().getContainer().classList.add('loaded-map');
    this.loadingDone.set(true);
  }

  private addHectometreLayer() {
    this.mapLayersApiService
      .addLayersFromApi(this.map(), HECTOMETRE_STYLE)
      .subscribe((addedLayerIds) => {
        addedLayerIds.forEach((layerId) => {
          const visible =
            this.mapOptions().find((option) => option.id === HECTOMETRE_STYLE.id)?.active ?? false;
          if (this.map()?.getLayer(layerId)) {
            this.map()?.setLayoutProperty(layerId, 'visibility', visible ? 'visible' : 'none');
          }
        });
      });
  }

  protected addClickableSourceAndLayers(
    definitions: SourceAndLayersDefinition[],
    clickHandler: (features: any, point: Point) => void,
  ) {
    definitions.forEach((def) => this.addSourceAndLayersToMap(def));

    const clickableLayerIds = definitions.flatMap((def) => def.layers).map((layer) => layer.id);

    this.registerClickHandlers(clickableLayerIds, clickHandler);
  }

  protected registerClickHandlers(
    layerIds: string[],
    clickHandler: (features: any, point: Point) => void,
  ) {
    // We want just one click handler for all clickable layers so if there are multiple features
    // under the clickpoint the topmost will be selected. If we register a clickhandler for each
    // layer seperately multiple handlers might trigger on one click.
    this.map().on('click', layerIds, (event: MapLayerMouseEvent) => {
      const features: MapGeoJSONFeature[] | undefined = event.features?.filter(
        (feature) => !!feature && !!feature.properties['id'],
      );

      if (features && features.length > 0) {
        clickHandler(features, event.point);
      }
    });
  }

  private addSourceAndLayersToMap(definition: SourceAndLayersDefinition) {
    this.map().addSource(definition.sourceId, this.EMPTY_GEOJSON_SOURCE);

    // Refresh whenever the features change
    effect(
      async () => {
        let source = this.map().getSource(definition.sourceId) as GeoJSONSource;
        if (source) {
          source.setData(definition.featureCollection());
        }
      },
      { injector: this.injector },
    );

    definition.layers.forEach((layer) => {
      this.map().addLayer(
        layer,
        this.mapLayerOrderingService.getLayerBeforeId(layer.id, this.map()),
      );
      this.map().on(
        'mouseenter',
        layer.id,
        () => (this.map().getCanvas().style.cursor = 'pointer'),
      );
      this.map().on('mouseleave', layer.id, () => (this.map().getCanvas().style.cursor = ''));
    });
  }

  protected addFcdLayers() {
    this.mapLayersApiService
      .addLayersFromApi(this.map(), FCD_VECTOR_STYLE)
      .subscribe((addedLayerIds) => {
        addedLayerIds.forEach((layerId) => {
          const visible =
            this.mapOptions().find((option) => option.id === FCD_VECTOR_STYLE.id)?.active ?? false;
          if (this.map()?.getLayer(layerId)) {
            this.map()?.setLayoutProperty(layerId, 'visibility', visible ? 'visible' : 'none');
          }
        });

        // FCD staleness is watched centrally so the per-map monitors don't fight over one banner;
        // point it at this (the currently loading) map.
        this.fcdFreshnessService.watch(this.map());
      });
  }

  zoomToIncidentArea(
    featureCollection: FeatureCollection<Polygon>,
    options?: { padding?: number; maxZoom?: number; durationMs?: number },
  ): void {
    const map = this.map();
    if (!map || !featureCollection?.features?.length) return;

    const padding = options?.padding ?? 40;
    const maxZoom = options?.maxZoom ?? 18;
    const duration = options?.durationMs ?? 800;

    const bbox = this.getBBoxFromPolygonFeatureCollection(featureCollection);
    if (!bbox) return;

    const timeout = this.userStateService.state.sidePanelAnimationFinished() ? 0 : 100;

    setTimeout(() => {
      map.fitBounds(
        [
          [bbox[0], bbox[1]],
          [bbox[2], bbox[3]],
        ],
        { padding, maxZoom, duration, bearing: this.navigation.active() ? map.getBearing() : 0 },
      );
    }, timeout);
  }

  private getBBoxFromPolygonFeatureCollection(fc: FeatureCollection<Polygon>): BBox | null {
    const positions = fc.features
      .flatMap((f) => f.geometry.coordinates)
      .flatMap((ring) => ring)
      .filter((p) => p?.length >= 2);

    if (positions.length === 0) return null;

    const lngs = positions.map((p) => p[0]);
    const lats = positions.map((p) => p[1]);

    return [Math.min(...lngs), Math.min(...lats), Math.max(...lngs), Math.max(...lats)];
  }

  moveMapTo(
    center: { lng: number; lat: number } | undefined,
    zoom?: number,
    onlyIfNotYetInView = false,
  ) {
    const map = this.map();

    if (!map || center === undefined) {
      return;
    }

    if (onlyIfNotYetInView) {
      const bounds = map.getBounds();
      const centerIsVisible = bounds?.contains([center.lng, center.lat]);
      if (centerIsVisible) {
        return;
      }
    }

    const currentCenter = map.getCenter();

    if (!this.areNearlyEqual(center, currentCenter)) {
      // if the sidepanel is still animating, wait a bit before moving the map.
      var timeout = this.userStateService.state.sidePanelAnimationFinished() ? 0 : 100;
      setTimeout(() => {
        map.flyTo({
          center,
          zoom,
          bearing: this.navigation.active() ? map.getBearing() : 0,
          animate: false,
        });
      }, timeout);
    }
  }

  public project(location: LngLatLike): Point | undefined {
    const point = this.map().project(location);
    if (!point) {
      return undefined;
    }
    return { x: point.x, y: point.y };
  }

  private areNearlyEqual(
    first: { lng: number; lat: number },
    second: { lng: number; lat: number },
  ): boolean {
    return (
      Math.abs(first.lng - second.lng) < this.COORDINATE_COMPARISON_PRECISION &&
      Math.abs(first.lat - second.lat) < this.COORDINATE_COMPARISON_PRECISION
    );
  }
}

export interface SourceAndLayersDefinition {
  sourceId: MapSourceId;
  layers: LayerSpecification[];
  featureCollection: Signal<FeatureCollection>;
}
